import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = {
  title: "", slug: "", summary: "", body_rich_text: "", origin: "", destination: "",
  airline: "", cabin_class: "economy", cash_price: "", points_price: "", taxes_and_fees: "",
  booking_url: "", expires_at: "", featured_image: "", is_featured: false, status: "published",
};

export default function DealsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/deals?limit=200").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { ...editing };
      ["cash_price", "points_price", "taxes_and_fees"].forEach((k) => { if (body[k] === "") body[k] = null; else body[k] = Number(body[k]); });
      if (editing.id) await api.put(`/deals/${editing.id}`, body);
      else await api.post("/deals", body);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => { if (!confirm("Delete?")) return; await api.delete(`/deals/${id}`); load(); };

  return (
    <div className="p-8 md:p-10">
      <div className="flex justify-between mb-6">
        <div><div className="overline text-[#B4533C]">Deals</div><h1 className="font-serif text-4xl mt-1">Travel Deal Alerts</h1></div>
        <button onClick={() => setEditing({ ...EMPTY })} className="bg-[#1B3B36] text-white px-5 py-2.5 text-sm" data-testid="new-deal-btn">+ New Deal</button>
      </div>
      <div className="border border-[#D6CFC4]">
        <table className="w-full admin-table">
          <thead className="bg-[#EFECE5]"><tr><th className="p-3 text-left">Title</th><th className="p-3 text-left">Route</th><th className="p-3 text-left">Price</th><th className="p-3 text-left">Expires</th><th></th></tr></thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-t border-[#D6CFC4]" data-testid={`deal-row-${d.id}`}>
                <td className="p-3"><button onClick={() => setEditing(d)} className="hover:text-[#B4533C]">{d.title}</button></td>
                <td className="p-3 text-xs font-mono">{d.origin} → {d.destination}</td>
                <td className="p-3 text-xs font-mono">{d.cash_price ? `$${d.cash_price}` : `${d.points_price?.toLocaleString() || "—"} pts`}</td>
                <td className="p-3 text-xs font-mono">{d.expires_at ? new Date(d.expires_at).toLocaleDateString() : "Open"}</td>
                <td className="p-3 text-right"><button onClick={() => del(d.id)} className="text-[#B4533C] text-xs">Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-6 overflow-y-auto">
          <div className="bg-white border border-[#D6CFC4] p-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h2 className="font-serif text-2xl mb-4">{editing.id ? "Edit" : "New"} Deal</h2>
            <div className="grid md:grid-cols-2 gap-3">
              <Field label="Title" v={editing.title} k="title" e={setEditing} />
              <Field label="Slug" v={editing.slug} k="slug" e={setEditing} />
              <Field label="Origin" v={editing.origin} k="origin" e={setEditing} />
              <Field label="Destination" v={editing.destination} k="destination" e={setEditing} />
              <Field label="Airline" v={editing.airline} k="airline" e={setEditing} />
              <SelectField label="Cabin" v={editing.cabin_class} k="cabin_class" e={setEditing} opts={["economy","premium-economy","business","first"]} />
              <Field label="Cash price $" v={editing.cash_price} k="cash_price" e={setEditing} type="number" />
              <Field label="Points price" v={editing.points_price} k="points_price" e={setEditing} type="number" />
              <Field label="Taxes & fees $" v={editing.taxes_and_fees} k="taxes_and_fees" e={setEditing} type="number" />
              <Field label="Booking URL" v={editing.booking_url} k="booking_url" e={setEditing} />
              <Field label="Expires at (ISO)" v={editing.expires_at} k="expires_at" e={setEditing} />
              <Field label="Featured image URL" v={editing.featured_image} k="featured_image" e={setEditing} />
              <SelectField label="Status" v={editing.status} k="status" e={setEditing} opts={["draft","published","expired"]} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!editing.is_featured} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} /> Featured</label>
            </div>
            <div className="mt-4">
              <label className="overline">Summary</label>
              <textarea value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} rows={3} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" />
            </div>
            <div className="mt-4">
              <label className="overline">Body (HTML)</label>
              <textarea value={editing.body_rich_text} onChange={(e) => setEditing({ ...editing, body_rich_text: e.target.value })} rows={6} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm font-mono" />
            </div>
            <div className="mt-5 flex gap-3">
              <button onClick={save} className="bg-[#1B3B36] text-white px-5 py-2.5 text-sm" data-testid="save-deal-btn">Save</button>
              <button onClick={() => setEditing(null)} className="border border-[#D6CFC4] px-5 py-2.5 text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Field({ label, v, k, e, type = "text" }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <input type={type} value={v || ""} onChange={(ev) => e((prev) => ({ ...prev, [k]: ev.target.value }))} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm" />
    </div>
  );
}
export function SelectField({ label, v, k, e, opts }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <select value={v || ""} onChange={(ev) => e((prev) => ({ ...prev, [k]: ev.target.value }))} className="w-full border border-[#D6CFC4] px-3 py-2 mt-1 text-sm">
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
